<template>
  <main class="books-page">

    <!-- =========================
         PAGE HEADER
    ========================== -->

    <section class="books-header">

      <div class="container">

        <div class="header-content">

          <span class="section-label">
            OUR COLLECTION
          </span>

          <h1>
            Explore our books
          </h1>

          <p>
            Discover stories, ideas, and worlds
            waiting for you.
          </p>

        </div>

      </div>

    </section>


    <!-- =========================
         CONTENT
    ========================== -->

    <section class="container books-content">

      <!-- Search + Filters -->

      <div class="filters-card">

        <div class="search-wrapper">

          <span class="search-icon">
            🔍
          </span>

          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search by title or author..."
          >

        </div>


        <div class="filter-group">

          <!-- Category -->

          <select
            v-model="selectedCategory"
            class="filter-select"
          >

            <option value="">
              All Categories
            </option>

            <option
              v-for="category in categories"
              :key="category"
              :value="category"
            >
              {{ category }}
            </option>

          </select>


          <!-- Sort -->

          <select
            v-model="sortBy"
            class="filter-select"
          >

            <option value="default">
              Sort By
            </option>

            <option value="name">
              Name A-Z
            </option>

            <option value="price-low">
              Price: Low to High
            </option>

            <option value="price-high">
              Price: High to Low
            </option>

            <option value="pages">
              Pages
            </option>

          </select>

        </div>

      </div>


      <!-- =========================
           CATEGORY COLLECTIONS
      ========================== -->

      <div
        v-if="
          !searchQuery &&
          !selectedCategory
        "
        class="collections-area"
      >

        <BookCollection
          title="Fiction"
          :books="booksByCategory('Fiction')"
          @view-all="
            openCategory('Fiction')
          "
        />


        <BookCollection
          title="Romance"
          :books="booksByCategory('Romance')"
          @view-all="
            openCategory('Romance')
          "
        />


        <BookCollection
          title="Classic"
          :books="booksByCategory('Classic')"
          @view-all="
            openCategory('Classic')
          "
        />


        <BookCollection
          title="Fantasy"
          :books="booksByCategory('Fantasy')"
          @view-all="
            openCategory('Fantasy')
          "
        />


        <BookCollection
          title="Historical Fiction"
          :books="
            booksByCategory(
              'Historical Fiction'
            )
          "
          @view-all="
            openCategory(
              'Historical Fiction'
            )
          "
        />


        <BookCollection
          title="Thriller"
          :books="booksByCategory('Thriller')"
          @view-all="
            openCategory('Thriller')
          "
        />


        <BookCollection
          title="Self Development"
          :books="
            booksByCategory(
              'Self Development'
            )
          "
          @view-all="
            openCategory(
              'Self Development'
            )
          "
        />


        <BookCollection
          title="Business"
          :books="booksByCategory('Business')"
          @view-all="
            openCategory('Business')
          "
        />


        <BookCollection
          title="Non-Fiction"
          :books="
            booksByCategory(
              'Non-Fiction'
            )
          "
          @view-all="
            openCategory(
              'Non-Fiction'
            )
          "
        />


        <BookCollection
          title="Biography"
          :books="booksByCategory('Biography')"
          @view-all="
            openCategory('Biography')
          "
        />

      </div>


      <!-- =========================
           ALL / FILTERED BOOKS
      ========================== -->

      <section class="all-books-section">

        <div class="all-books-header">

          <div>

            <span class="section-label">
              COLLECTION
            </span>

            <h2>

              <span
                v-if="selectedCategory"
              >
                {{ selectedCategory }}
              </span>

              <span v-else>
                All Books
              </span>

            </h2>

            <p>
              {{ filteredBooks.length }}
              books found
            </p>

          </div>


          <!-- Seller -->

          <router-link
            v-if="isSeller"
            to="/books/add"
            class="add-book-btn"
          >
            + Add Book
          </router-link>

        </div>


        <!-- Loading -->

        <div
          v-if="loading"
          class="loading-container"
        >

          <div
            class="spinner-border"
          ></div>

          <p>
            Loading books...
          </p>

        </div>


        <!-- No Books -->

        <div
          v-else-if="
            filteredBooks.length === 0
          "
          class="empty-state"
        >

          <div class="empty-icon">
            📚
          </div>

          <h3>
            No books found
          </h3>

          <p>
            Try another search or category.
          </p>

          <button
            class="clear-btn"
            @click="clearFilters"
          >
            Clear Filters
          </button>

        </div>


        <!-- Books -->

        <div
          v-else
          class="books-grid"
        >

          <BookCard
            v-for="book in paginatedBooks"
            :key="
              book.id ||
              book.ISBN
            "
            :book="book"
            :wishlist="wishlist"
            :can-manage="false"
          />

        </div>


        <!-- Pagination -->

        <div
          v-if="totalPages > 1"
          class="pagination-wrapper"
        >

          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="goToPage(
              currentPage - 1
            )"
          >
            ←
          </button>


          <button
            v-for="page in totalPages"
            :key="page"
            class="page-btn"
            :class="{
              active:
                currentPage === page
            }"
            @click="
              goToPage(page)
            "
          >
            {{ page }}
          </button>


          <button
            class="page-btn"
            :disabled="
              currentPage === totalPages
            "
            @click="goToPage(
              currentPage + 1
            )"
          >
            →
          </button>

        </div>

      </section>

    </section>

  </main>
</template>


<script>
import BookCard
  from '../components/BookCard/BookCard.vue'

import BookCollection
  from '../components/BookCollection/BookCollection.vue'

export default {

  name: 'Books',

  components: {
    BookCard,
    BookCollection
  },


  data() {

    return {

      searchQuery:
        '',

      selectedCategory:
        '',

      sortBy:
        'default',

      currentPage:
        1,

      booksPerPage:
        12

    }

  },


  computed: {

    books() {

      return (
        this.$store.state.books ||
        []
      )

    },


    wishlist() {

      return (
        this.$store.state.wishlist ||
        []
      )

    },


    loading() {

      return (
        this.$store.state.loading ||
        false
      )

    },


    userRole() {

      const savedUser =
        localStorage.getItem(
          'currentUser'
        )

      if (!savedUser) {
        return null
      }

      try {

        return JSON.parse(
          savedUser
        ).role

      } catch (error) {

        return null

      }

    },


    isSeller() {

      return (
        this.userRole ===
        'seller'
      )

    },


    categories() {

      return [
        'Fiction',
        'Romance',
        'Classic',
        'Fantasy',
        'Historical Fiction',
        'Thriller',
        'Self Development',
        'Business',
        'Non-Fiction',
        'Biography'
      ]

    },


    filteredBooks() {

      let result =
        [...this.books]


      /* CATEGORY */

      if (
        this.selectedCategory
      ) {

        result =
          result.filter(
            book =>
              book.category ===
              this.selectedCategory
          )

      }


      /* SEARCH */

      if (
        this.searchQuery
          .trim()
      ) {

        const search =
          this.searchQuery
            .toLowerCase()
            .trim()

        result =
          result.filter(
            book => {

              const name =
                String(
                  book.name || ''
                ).toLowerCase()

              const author =
                String(
                  book.author || ''
                ).toLowerCase()

              return (
                name.includes(search) ||
                author.includes(search)
              )

            }
          )

      }


      /* SORT */

      if (
        this.sortBy ===
        'name'
      ) {

        result.sort(
          (a, b) =>
            a.name.localeCompare(
              b.name
            )
        )

      }


      if (
        this.sortBy ===
        'price-low'
      ) {

        result.sort(
          (a, b) =>
            Number(a.price) -
            Number(b.price)
        )

      }


      if (
        this.sortBy ===
        'price-high'
      ) {

        result.sort(
          (a, b) =>
            Number(b.price) -
            Number(a.price)
        )

      }


      if (
        this.sortBy ===
        'pages'
      ) {

        result.sort(
          (a, b) =>
            Number(b.pages) -
            Number(a.pages)
        )

      }


      return result

    },


    totalPages() {

      return Math.ceil(
        this.filteredBooks.length /
        this.booksPerPage
      )

    },


    paginatedBooks() {

      const start =
        (
          this.currentPage -
          1
        ) *
        this.booksPerPage

      const end =
        start +
        this.booksPerPage

      return this.filteredBooks.slice(
        start,
        end
      )

    }

  },


  watch: {

    searchQuery() {

      this.currentPage =
        1

    },


    selectedCategory() {

      this.currentPage =
        1

    },


    sortBy() {

      this.currentPage =
        1

    },


    '$route.query.category': {

      immediate:
        true,

      handler(category) {

        this.selectedCategory =
          category || ''

      }

    }

  },


  methods: {

    booksByCategory(
      category
    ) {

      return this.books.filter(
        book =>
          book.category ===
          category
      )

    },


    openCategory(
      category
    ) {

      this.$router.push({
        path:
          '/books',

        query: {
          category:
            category
        }
      })

    },


    goToPage(
      page
    ) {

      if (
        page < 1 ||
        page >
          this.totalPages
      ) {

        return

      }

      this.currentPage =
        page

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })

    },


    clearFilters() {

      this.searchQuery =
        ''

      this.selectedCategory =
        ''

      this.sortBy =
        'default'

      this.currentPage =
        1

      if (
        this.$route.query.category
      ) {

        this.$router.push({
          path:
            '/books'
        })

      }

    }

  }

}
</script>


<style scoped>

/* ========================================
   PAGE
======================================== */

.books-page {
  min-height:
    100vh;

  background:
    var(--background);

  color:
    var(--text);
}


/* ========================================
   HEADER
======================================== */

.books-header {
  position:
    relative;

  padding:
    75px 0;

  overflow:
    hidden;

  background:
    linear-gradient(
      120deg,
      #321450,
      #5d267f,
      #9b59b6
    );
}


.books-header::after {
  content:
    '';

  position:
    absolute;

  width:
    350px;

  height:
    350px;

  border-radius:
    50%;

  right:
    -100px;

  top:
    -170px;

  border:
    1px solid
    rgba(255,255,255,0.18);
}


.header-content {
  position:
    relative;

  z-index:
    2;

  max-width:
    700px;
}


.books-header .section-label {
  color:
    #ddc9ff;
}


.books-header h1 {
  color:
    #ffffff;

  font-size:
    clamp(
      2.5rem,
      5vw,
      4.5rem
    );

  font-weight:
    800;

  margin:
    8px 0 12px;
}


.books-header p {
  color:
    rgba(255,255,255,0.75);

  font-size:
    1.05rem;

  margin:
    0;
}


/* ========================================
   CONTENT
======================================== */

.books-content {
  padding-top:
    45px;

  padding-bottom:
    80px;
}


/* ========================================
   FILTERS
======================================== */

.filters-card {
  display:
    flex;

  align-items:
    center;

  gap:
    15px;

  padding:
    18px;

  margin-bottom:
    55px;

  background:
    var(--surface);

  border:
    1px solid
    var(--border);

  border-radius:
    16px;

  box-shadow:
    0 10px 30px
    rgba(70,40,100,0.07);
}


.search-wrapper {
  position:
    relative;

  flex:
    1;
}


.search-icon {
  position:
    absolute;

  left:
    15px;

  top:
    50%;

  transform:
    translateY(-50%);

  font-size:
    0.95rem;
}


.search-input {
  width:
    100%;

  height:
    48px;

  padding:
    0 15px 0 45px;

  border:
    1px solid
    var(--border);

  border-radius:
    10px;

  background:
    var(--background);

  color:
    var(--text);

  outline:
    none;
}


.search-input:focus {
  border-color:
    var(--primary);

  box-shadow:
    0 0 0 3px
    rgba(124,58,237,0.1);
}


.filter-group {
  display:
    flex;

  gap:
    10px;
}


.filter-select {
  height:
    48px;

  min-width:
    175px;

  padding:
    0 12px;

  border:
    1px solid
    var(--border);

  border-radius:
    10px;

  background:
    var(--background);

  color:
    var(--text);

  outline:
    none;

  cursor:
    pointer;
}


.filter-select:focus {
  border-color:
    var(--primary);
}


/* ========================================
   COLLECTIONS
======================================== */

.collections-area {
  display:
    flex;

  flex-direction:
    column;

  gap:
    65px;

  margin-bottom:
    80px;
}


/* ========================================
   ALL BOOKS
======================================== */

.all-books-section {
  padding-top:
    20px;
}


.all-books-header {
  display:
    flex;

  justify-content:
    space-between;

  align-items:
    flex-end;

  gap:
    20px;

  margin-bottom:
    30px;
}


.all-books-header h2 {
  color:
    var(--text);

  font-size:
    2rem;

  font-weight:
    800;

  margin:
    4px 0;
}


.all-books-header p {
  color:
    var(--text-secondary);

  margin:
    0;
}


.add-book-btn {
  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  padding:
    11px 18px;

  border-radius:
    10px;

  background:
    var(--primary);

  color:
    white;

  text-decoration:
    none;

  font-weight:
    700;
}


.add-book-btn:hover {
  color:
    white;

  opacity:
    0.9;
}


/* ========================================
   BOOK GRID
======================================== */

.books-grid {
  display:
    grid;

  grid-template-columns:
    repeat(3, 1fr);

  gap:
    24px;
}


/* ========================================
   LOADING
======================================== */

.loading-container {
  min-height:
    350px;

  display:
    flex;

  flex-direction:
    column;

  justify-content:
    center;

  align-items:
    center;
}


.loading-container .spinner-border {
  color:
    var(--primary);
}


.loading-container p {
  margin-top:
    15px;

  color:
    var(--text-secondary);
}


/* ========================================
   EMPTY
======================================== */

.empty-state {
  text-align:
    center;

  padding:
    80px 20px;

  background:
    var(--surface);

  border:
    1px solid
    var(--border);

  border-radius:
    20px;
}


.empty-icon {
  font-size:
    3rem;

  margin-bottom:
    15px;
}


.empty-state h3 {
  color:
    var(--text);

  font-weight:
    800;
}


.empty-state p {
  color:
    var(--text-secondary);
}


.clear-btn {
  border:
    none;

  background:
    var(--primary);

  color:
    white;

  padding:
    10px 18px;

  border-radius:
    9px;

  font-weight:
    700;
}


/* ========================================
   PAGINATION
======================================== */

.pagination-wrapper {
  display:
    flex;

  justify-content:
    center;

  align-items:
    center;

  gap:
    7px;

  margin-top:
    45px;
}


.page-btn {
  min-width:
    40px;

  height:
    40px;

  padding:
    0 10px;

  border:
    1px solid
    var(--border);

  border-radius:
    9px;

  background:
    var(--surface);

  color:
    var(--text);

  font-weight:
    600;

  cursor:
    pointer;

  transition:
    all 0.2s ease;
}


.page-btn:hover:not(:disabled) {
  border-color:
    var(--primary);

  color:
    var(--primary);
}


.page-btn.active {
  background:
    var(--primary);

  color:
    white;

  border-color:
    var(--primary);
}


.page-btn:disabled {
  opacity:
    0.4;

  cursor:
    not-allowed;
}


/* ========================================
   DARK MODE
======================================== */

:global(body.dark-theme) .books-page {
  background:
    #0f0b18;
}


:global(body.dark-theme) .filters-card {
  background:
    #181225;

  border-color:
    #302441;
}


:global(body.dark-theme) .search-input,
:global(body.dark-theme) .filter-select {
  background:
    #100c18;

  color:
    #f5f1ff;

  border-color:
    #3a2d49;
}


:global(body.dark-theme) .search-input::placeholder {
  color:
    #8f8599;
}


:global(body.dark-theme) .empty-state {
  background:
    #181225;

  border-color:
    #302441;
}


:global(body.dark-theme) .page-btn {
  background:
    #181225;

  color:
    #f5f1ff;

  border-color:
    #3a2d49;
}


/* ========================================
   RESPONSIVE
======================================== */

@media (max-width: 1000px) {

  .books-grid {
    grid-template-columns:
      repeat(2, 1fr);
  }

  .filters-card {
    flex-direction:
      column;

    align-items:
      stretch;
  }

  .filter-group {
    width:
      100%;
  }

  .filter-select {
    flex:
      1;
  }

}


@media (max-width: 700px) {

  .books-header {
    padding:
      55px 0;
  }

  .books-grid {
    grid-template-columns:
      1fr;
  }

  .all-books-header {
    align-items:
      flex-start;

    flex-direction:
      column;
  }

}


@media (max-width: 500px) {

  .filter-group {
    flex-direction:
      column;
  }

  .filter-select {
    width:
      100%;
  }

  .books-header h1 {
    font-size:
      2.5rem;
  }

}

</style>